/**
 * Text helpers of `NxTemplateInput`: where an open `{{…` sits before the
 * caret, and how a `{{key}}` goes in.
 */

/** A placeholder the field can insert as `{{key}}`. */
export interface NxTemplateSuggestion {
  key: string;
  label: string;
  /** Heading the suggestion is listed under. */
  group?: string;
  /** Short explanation or an example value. */
  description?: string;
}

/**
 * The open `{{partial` right before `caret`, if any: `from` is the index of
 * the braces, `query` what was typed after them. `null` when the caret is not
 * inside an unfinished placeholder.
 */
export function openPlaceholderAt(
  text: string,
  caret: number
): { from: number; query: string } | null {
  const before = text.slice(0, caret);
  const start = before.lastIndexOf('{{');
  if (start < 0) return null;
  const query = before.slice(start + 2);
  if (!/^[\w.-]*$/.test(query)) return null;
  return { from: start, query };
}

/**
 * Puts `{{key}}` into `text`, replacing `start`–`end` (a selection or an open
 * `{{partial`). A `}}` that already follows the caret is taken over rather
 * than doubled. Returns the new text and the caret after the placeholder.
 */
export function insertPlaceholder(
  text: string,
  start: number,
  end: number,
  key: string
): { text: string; caret: number } {
  const after = text.slice(end);
  const tail = after.startsWith('}}') ? after.slice(2) : after;
  const token = `{{${key}}}`;
  return { text: text.slice(0, start) + token + tail, caret: start + token.length };
}

/** Suggestions whose key, label or description contain `query` (case-insensitive). */
export function filterSuggestions(
  suggestions: readonly NxTemplateSuggestion[],
  query: string
): NxTemplateSuggestion[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...suggestions];
  return suggestions.filter(
    (s) =>
      s.key.toLowerCase().includes(q) ||
      s.label.toLowerCase().includes(q) ||
      (s.description ?? '').toLowerCase().includes(q)
  );
}
